import { t } from "@lingui/macro";
import { ArrowsOut, Minus, Plus } from "@phosphor-icons/react";
import { isGenericSkillName, type ResumeDraft } from "@reactive-resume/schema";
import { pageSizeMap } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  type ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";
import webfontloader from "webfontloader";

type Props = {
  draft: ResumeDraft;
  fallbackName?: string;
  reading?: boolean;
  writing?: boolean;
};

const MM_TO_PX = 3.78;
const PAGE_W = pageSizeMap.a4.width * MM_TO_PX;
const PAGE_H = pageSizeMap.a4.height * MM_TO_PX;
const PAGE_GAP = 20;
const PAGE_PAD = 18;

const stripHtml = (value?: string): string =>
  (value ?? "")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const bullets = (value?: string): string[] =>
  stripHtml(value)
    .split("\n")
    .map((line) => line.replace(/^•\s*/, "").trim())
    .filter(Boolean);

const joinDot = (parts: Array<string | undefined>) => parts.filter(Boolean).join(" · ");

const Heading = ({ children }: { children: string }) => (
  <h3 className="mb-1.5 border-b border-neutral-900/20 pb-[3px] text-[0.72em] font-semibold uppercase tracking-[0.18em]">
    {children}
  </h3>
);

export const ResumePreview = ({ draft, fallbackName, reading, writing }: Props) => {
  const name = draft.basics?.name?.trim() || fallbackName?.trim() || t`Your name`;
  const headline = draft.basics?.headline?.trim();
  const location = draft.basics?.location?.trim();
  const email = draft.basics?.email?.trim();
  const phone = draft.basics?.phone?.trim();
  const experience = (draft.experience ?? []).filter((item) => item.company?.trim());
  const education = (draft.education ?? []).filter((item) => item.institution?.trim());
  const skillLines = (draft.skills ?? [])
    .map((item) => {
      const keywords = (item.keywords ?? []).map((keyword) => keyword.trim()).filter(Boolean);
      if (keywords.length === 0) return null;
      const group = isGenericSkillName(item.name) ? "" : item.name?.trim() ?? "";
      return { name: group, keywords };
    })
    .filter((item): item is { name: string; keywords: string[] } => item !== null);
  const projects = (draft.projects ?? []).filter((item) => item.name?.trim());
  const summary = stripHtml(draft.summary);
  const bulletCap = 5;

  const stageRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const atFit = useRef(true);
  const [pages, setPages] = useState(1);
  const [scale, setScale] = useState(0.5);

  const stackH = pages * PAGE_H + (pages - 1) * PAGE_GAP;

  const fitToView = useCallback(
    (duration = 200) => {
      const ref = transformRef.current;
      const wrapper = ref?.instance.wrapperComponent ?? stageRef.current;
      if (!ref || !wrapper) return;
      const vw = wrapper.clientWidth;
      const vh = wrapper.clientHeight;
      if (vw < 8 || vh < 8) return;
      const availW = Math.max(vw - 48, 1);
      const availH = Math.max(vh - 48, 1);
      const next = Math.min(availW / PAGE_W, availH / stackH);
      const fit = Number.isFinite(next) && next > 0.12 ? next : 0.4;
      const x = (vw - PAGE_W * fit) / 2;
      const y = (vh - stackH * fit) / 2;
      atFit.current = true;
      ref.setTransform(x, y, fit, duration);
    },
    [stackH],
  );

  useLayoutEffect(() => {
    webfontloader.load({
      events: false,
      classes: false,
      google: { families: ["Lora:regular,600,700"] },
    });
  }, []);

  useLayoutEffect(() => {
    const measure = measureRef.current;
    if (!measure) return;
    const update = () => {
      const contentH = Math.max(measure.scrollHeight, 1);
      setPages(Math.max(1, Math.ceil(contentH / PAGE_H)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(measure);
    return () => ro.disconnect();
  }, [draft, writing, reading, name, headline, location, email, phone, summary]);

  useLayoutEffect(() => {
    if (!atFit.current) return;
    const frame = window.requestAnimationFrame(() => fitToView(0));
    return () => window.cancelAnimationFrame(frame);
  }, [fitToView, pages]);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const onResize = () => {
      if (atFit.current) fitToView(0);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(stage);
    window.addEventListener("resize", onResize);
    const retry = window.setTimeout(() => onResize(), 50);

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) event.preventDefault();
      event.stopPropagation();
    };
    stage.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.clearTimeout(retry);
      stage.removeEventListener("wheel", onWheel);
    };
  }, [fitToView]);

  const markUserZoom = () => {
    atFit.current = false;
  };

  const body = (
    <div
      className="space-y-[0.8rem] text-neutral-900"
      style={{ padding: PAGE_PAD, fontSize: 14, lineHeight: 1.35 }}
    >
      <header className="pb-1">
        <AnimatePresence mode="wait">
          <motion.h2
            key={name}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[1.7em] font-semibold leading-[1.1] tracking-[-0.02em]"
          >
            {name}
          </motion.h2>
        </AnimatePresence>
        {headline ? (
          <p className="mt-1 text-[0.78em] font-medium uppercase tracking-[0.16em] text-neutral-900/70">
            {headline}
          </p>
        ) : (
          <p className="mt-1 text-[0.78em] font-medium uppercase tracking-[0.16em] text-neutral-400">
            {t`Your title`}
          </p>
        )}
        <p className="mt-2 text-[0.82em] leading-relaxed">
          {location ? (
            <span>{location}</span>
          ) : (
            <span className="text-neutral-500">{t`Remote`}</span>
          )}
          <span className="text-neutral-900/35 mx-[0.45em]">·</span>
          {email ? (
            <span>{email}</span>
          ) : (
            <span className="text-neutral-400">{t`email@domain.com`}</span>
          )}
          {phone ? (
            <>
              <span className="text-neutral-900/35 mx-[0.45em]">·</span>
              <span>{phone}</span>
            </>
          ) : null}
        </p>
      </header>

      {summary ? (
        <section>
          <Heading>{t`Summary`}</Heading>
          <p className="text-[0.95em] leading-relaxed text-neutral-800">{summary}</p>
        </section>
      ) : null}

      <section>
        <Heading>{t`Experience`}</Heading>
        <div className="grid gap-y-[0.85em]">
          {experience.length === 0 && (
            <p className="text-[0.95em] text-neutral-400">
              {reading || writing
                ? t`Reading your notes…`
                : t`Company, title, and Month Year - Present land here.`}
            </p>
          )}
          {experience.map((item, index) => {
            const lines = bullets(item.summary);
            const title = item.position?.trim() || item.company;
            const subtitle = joinDot([item.position ? item.company : undefined, item.location]);
            return (
              <motion.div
                key={`${item.company}-${item.position}-${index}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-1"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <p className="min-w-0 font-semibold leading-snug">{title}</p>
                  {item.date ? (
                    <p className="shrink-0 text-[0.9em] tabular-nums text-neutral-900/70">
                      {item.date}
                    </p>
                  ) : (
                    <p className="shrink-0 text-[0.9em] text-neutral-400">{t`Month Year - Present`}</p>
                  )}
                </div>
                {subtitle && (
                  <p className="text-[0.95em] leading-snug text-neutral-900/80">{subtitle}</p>
                )}
                {lines.length > 0 ? (
                  <ul className="mt-[0.2em] space-y-[0.15em] pl-[1.15em]">
                    {lines.slice(0, bulletCap).map((line) => (
                      <li
                        key={line}
                        className="list-disc text-[0.92em] leading-snug text-neutral-900/80"
                      >
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : writing ? (
                  <div className="space-y-1.5 pt-1">
                    <div className="h-2 w-[92%] animate-pulse rounded-full bg-neutral-200" />
                    <div className="h-2 w-[78%] animate-pulse rounded-full bg-neutral-200" />
                    <div className="h-2 w-[86%] animate-pulse rounded-full bg-neutral-200" />
                  </div>
                ) : null}
              </motion.div>
            );
          })}
        </div>
      </section>

      <section>
        <Heading>{t`Education`}</Heading>
        {education.length > 0 ? (
          <div className="grid gap-y-2">
            {education.map((item, index) => (
              <div
                key={`${item.institution}-${index}`}
                className="flex items-baseline justify-between gap-4"
              >
                <div>
                  <p className="font-semibold leading-snug">{item.institution}</p>
                  <p className="text-[0.95em] text-neutral-900/80">
                    {joinDot([item.studyType, item.area]) || t`Degree · Field of study`}
                  </p>
                </div>
                {item.date && (
                  <p className="shrink-0 text-[0.9em] tabular-nums text-neutral-900/70">
                    {item.date}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-neutral-400">
            <p className="font-semibold">{t`College or university`}</p>
            <p className="text-[0.95em]">{t`Degree · Field of study`}</p>
          </div>
        )}
      </section>

      <section>
        <Heading>{t`Projects`}</Heading>
        {projects.length > 0 ? (
          <div className="grid gap-y-2">
            {projects.slice(0, 3).map((item, index) => (
              <div key={`${item.name}-${index}`}>
                <p className="font-semibold leading-snug">{item.name}</p>
            {item.summary && (
                  <ul className="mt-[0.2em] space-y-[0.15em] pl-[1.15em]">
                    {bullets(item.summary)
                      .slice(0, 2)
                      .map((line) => (
                        <li
                          key={line}
                          className="list-disc text-[0.92em] leading-snug text-neutral-900/80"
                        >
                          {line}
                        </li>
                      ))}
                  </ul>
                )}
                {(item.keywords ?? []).length > 0 && (
                  <p className="text-[0.9em] text-neutral-900/70">
                    {item.keywords?.slice(0, 6).join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[0.95em] text-neutral-400">{t`A shipped project or coursework piece`}</p>
        )}
      </section>

      <section>
        <Heading>{t`Skills`}</Heading>
        {skillLines.length > 0 ? (
          <div className="grid gap-y-1">
            {skillLines.slice(0, 4).map((item) => (
              <p key={`${item.name}-${item.keywords.join(",")}`} className="leading-snug">
                {item.name ? <span className="font-semibold">{item.name} </span> : null}
                <span className="text-neutral-900/80">{item.keywords.slice(0, 8).join(", ")}</span>
              </p>
            ))}
          </div>
        ) : (
          <p className="text-[0.95em] text-neutral-400">{t`Languages · Tools · Practices`}</p>
        )}
      </section>
    </div>
  );

  const pageLabel = pages === 1 ? t`A4 · 1 page` : t`A4 · ${pages} pages`;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="mb-1.5 flex shrink-0 items-center justify-between gap-2 px-0.5">
        <p className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/70">
          {t`Preview`}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-border/80 bg-background/80">
            <button
              type="button"
              aria-label={t`Zoom out`}
              onClick={() => {
                markUserZoom();
                transformRef.current?.zoomOut(0.2);
              }}
              className="flex h-7 w-7 items-center justify-center text-foreground/70 hover:text-foreground"
            >
              <Minus size={12} weight="bold" />
            </button>
            <span className="min-w-[2.6rem] text-center text-[11px] font-medium tabular-nums text-foreground/80">
              {`${Math.round(scale * 100)}%`}
            </span>
            <button
              type="button"
              aria-label={t`Zoom in`}
              onClick={() => {
                markUserZoom();
                transformRef.current?.zoomIn(0.2);
              }}
              className="flex h-7 w-7 items-center justify-center text-foreground/70 hover:text-foreground"
            >
              <Plus size={12} weight="bold" />
            </button>
            <button
              type="button"
              aria-label={t`Fit to view`}
              onClick={() => fitToView(200)}
              className="flex h-7 items-center gap-1 border-l border-border/80 px-1.5 text-[10px] font-semibold text-foreground/70 hover:text-foreground"
            >
              <ArrowsOut size={11} />
              <span className="hidden sm:inline">{t`Fit`}</span>
            </button>
          </div>
          <p className="hidden text-[11px] font-medium tabular-nums text-foreground/70 sm:block">
            {pageLabel}
          </p>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden overscroll-none">
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 bg-white"
          style={{
            fontFamily: "Lora, Georgia, serif",
            width: PAGE_W,
            visibility: "hidden",
            zIndex: -1,
          }}
        >
          {body}
        </div>

        <div ref={stageRef} className="absolute inset-0 overflow-hidden">
          <TransformWrapper
            ref={transformRef}
            minScale={0.2}
            maxScale={2.5}
            initialScale={0.45}
            limitToBounds={false}
            centerOnInit={false}
            alignmentAnimation={{ disabled: true }}
            velocityAnimation={{ disabled: true }}
            doubleClick={{ disabled: true }}
            panning={{ velocityDisabled: true }}
            wheel={{ step: 0.18, smoothStep: 0.008 }}
            pinch={{ step: 5 }}
            onInit={() => {
              window.requestAnimationFrame(() => fitToView(0));
            }}
            onWheel={() => markUserZoom()}
            onPanning={() => markUserZoom()}
            onPinching={() => markUserZoom()}
            onTransformed={(_, state) => setScale(state.scale)}
          >
            <TransformComponent
              wrapperClass="!h-full !w-full !overflow-hidden"
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentClass="!h-max !w-max"
              contentStyle={{ width: PAGE_W, height: stackH }}
            >
              <div className="flex flex-col" style={{ width: PAGE_W, gap: PAGE_GAP }}>
                {Array.from({ length: pages }, (_, index) => (
                  <article
                    key={index}
                    className="relative overflow-hidden bg-white text-neutral-900 shadow-[0_24px_60px_-28px_rgba(0,0,0,0.55)] ring-1 ring-black/10"
                    style={{
                      width: PAGE_W,
                      height: PAGE_H,
                      fontFamily: "Lora, Georgia, serif",
                    }}
                  >
                    {writing && index === 0 && (
                      <div className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden bg-neutral-200">
                        <motion.div
                          className="h-full bg-neutral-900"
                          initial={{ x: "-100%" }}
                          animate={{ x: "100%" }}
                          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                        />
                      </div>
                    )}
                    <div style={{ transform: `translateY(${-PAGE_H * index}px)` }}>{body}</div>
                  </article>
                ))}
              </div>
            </TransformComponent>
          </TransformWrapper>
        </div>
      </div>
    </div>
  );
};
